import Swal from 'sweetalert2';

/**
 * AB#21827 — every toast and pop-up follows the theme. SweetAlert paints its
 * own white popup and blue/red buttons unless told otherwise, so read the
 * portal's tokens at fire time (the theme can change while the app is open)
 * and pass them in; main.scss carries the same rules for the parts SweetAlert
 * does not expose as options.
 */
const token = (name: string, fallback: string): string => {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
};
const themed = () => ({
  background: token('--surface', '#ffffff'),
  color: token('--ink', '#1b2430'),
  confirmButtonColor: token('--accent', '#3b5bdb'),
  cancelButtonColor: token('--danger', '#c62828'),
});

/**
 * Show a simple alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {"success" | "error" | "warning" | "info" | "question"} icon - Alert icon type
 */
export const showAlert = (
  title: string,
  text: string,
  icon: 'success' | 'error' | 'warning' | 'info' | 'question'
): void => {
  Swal.fire({
    title,
    text,
    icon,
    ...themed(),
  });
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/**
 * AB#22394 — an alert with a short intro and a list of lines (e.g. the rows of an uploaded
 * file that failed). Stays open until dismissed; every line is escaped.
 */
export const showListAlert = (
  title: string,
  intro: string,
  lines: string[],
  icon: 'success' | 'error' | 'warning' | 'info' | 'question'
): void => {
  const items = lines.map((line) => `<li>${escapeHtml(line)}</li>`).join('');
  Swal.fire({
    title,
    html:
      `<p>${escapeHtml(intro)}</p>` +
      `<ul style="text-align:left;margin:12px 0 0;padding-left:18px;font-size:0.875rem;line-height:1.5">${items}</ul>`,
    icon,
    ...themed(),
  });
};

/**
 * Show a confirmation dialog
 * @param {string} title - Confirmation title
 * @param {string} text - Confirmation message
 * @returns {Promise<boolean>} - Returns `true` if confirmed, `false` if canceled
 */
export const showConfirm = async (title: string, text: string): Promise<boolean> => {
  const result = await Swal.fire({
    title,
    text,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    ...themed(),
  });
  return result.isConfirmed;
};

/**
 * Show a toast notification
 * @param {string} message - Toast message
 * @param {"success" | "error" | "warning" | "info"} icon - Toast type
 */
export const showToast = (
  message: string,
  icon: 'success' | 'error' | 'warning' | 'info' = 'success'
): void => {
  // A confirmation is glanced at; an error has to be read, so it stays
  // longer and can be closed by hand.
  const isError = icon === 'error' || icon === 'warning';
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title: message,
    showConfirmButton: false,
    showCloseButton: isError,
    timer: isError ? 8000 : 3000,
    timerProgressBar: true,
    background: themed().background,
    color: themed().color,
  });
};

/**
 * Show a timer alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message
 * @param {"success" | "error" | "warning" | "info" | "question"} icon - Alert icon type
 * @param {number} timer - Duration in milliseconds before auto-close (default: 3000ms)
 */
export const showTimerAlert = (
  title: string,
  text: string,
  icon: 'success' | 'error' | 'warning' | 'info' | 'question',
  timer: number = 3000
): void => {
  Swal.fire({
    title,
    text,
    icon,
    timer,
    timerProgressBar: true,
    ...themed(),
  });
};
